import { useEffect, useRef, useState } from 'react'
import { BackupView } from '../auth/SignInModal.jsx'
import { displayNameOf, isLikelyValidNip05 } from '../../utils/profile.js'
import { Nip05Display } from '../Nip05Display.jsx'
import { uploadImageToNostrBuild, MAX_UPLOAD_BYTES } from '../../utils/nostrBuild.js'

const EMPTY_FORM = {
  displayName: '',
  name: '',
  picture: '',
  nip05: '',
  about: '',
}
// Soft cap on the about field. Familiar mental model from Twitter's old
// limit, covers most real Nostr bios. We compute the actual maxLength
// at render time as max(ABOUT_LIMIT, currentLength) so an imported bio
// longer than 280 stays editable instead of being locked out of edits.
const ABOUT_LIMIT = 280

// Account surface opened by clicking the header chip. Three sections:
// public key (with Copy), recovery key (gated by canExportNsec — only
// local and local-encrypted accounts can reveal their nsec from this
// device), and Sign out.
//
// When the user clicks Export recovery key we swap the entire modal
// body for the BackupView from SignInModal so the password-manager
// hint form works the same way as the signup flow. UX28 renamed the
// user-facing vocab from "nsec/secret key" to "recovery key".
export function AccountModal({
  open,
  profile,
  pubkey,
  npub,
  loginType,
  locked,
  canExportNsec,
  canLinkGoogle, // UX29 Phase 2c
  nip05Verified, // T16: 'verified' | 'unverified' | 'unknown' from the lifted hook
  onSign,        // UX32: raw auth.sign for NIP-98 upload auth
  onExportNsec,
  onLinkGoogle,  // UX29 Phase 2c
  onLogout,
  onUnlock,
  onClose,
  onUpdateProfile,
}) {
  const [exported, setExported] = useState(null) // { pubkey, nsec } | null
  const [copied, setCopied] = useState(false)
  const [imgFailed, setImgFailed] = useState(false)
  const [exportError, setExportError] = useState(null)
  // Profile editor state.
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  // UX32: picture upload state. 'idle' | 'uploading' | 'error'.
  // showPicUrl toggles the "Paste URL instead" fallback input.
  const [picUploadStatus, setPicUploadStatus] = useState('idle')
  const [picUploadError, setPicUploadError] = useState(null)
  const [showPicUrl, setShowPicUrl] = useState(false)
  const fileInputRef = useRef(null)

  // Escape to close. Matches the SignInModal pattern from UX03.
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Reset transient state when the modal closes so a re-open is fresh.
  useEffect(() => {
    if (!open) {
      setExported(null)
      setCopied(false)
      setImgFailed(false)
      setExportError(null)
      setEditing(false)
      setForm(EMPTY_FORM)
      setSaving(false)
      setSaveError(null)
      setPicUploadStatus('idle')
      setPicUploadError(null)
      setShowPicUrl(false)
    }
  }, [open])

  // UX32: file picker handler. Delegates to the nostr.build helper and
  // populates form.picture with the returned URL. Errors stay inline.
  const handlePictureUpload = async (e) => {
    const file = e.target?.files?.[0]
    if (!file) return
    setPicUploadError(null)
    setPicUploadStatus('uploading')
    try {
      const url = await uploadImageToNostrBuild(file, onSign)
      setForm((prev) => ({ ...prev, picture: url }))
      setPicUploadStatus('idle')
    } catch (err) {
      setPicUploadError(err?.message || 'Upload failed.')
      setPicUploadStatus('error')
    } finally {
      // Clear the file input so re-selecting the same file re-fires change.
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  if (!open) return null

  const label = displayNameOf(profile, npub)
  const initial = (label?.[0] || '?').toUpperCase()
  const showImg = profile?.picture && !imgFailed

  const copyNpub = async () => {
    if (!npub) return
    try {
      await navigator.clipboard.writeText(npub)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch { /* noop */ }
  }

  const handleExport = () => {
    setExportError(null)
    const nsec = onExportNsec?.()
    if (nsec) {
      setExported({ pubkey, nsec })
    } else {
      setExportError('Could not read the key from this device.')
    }
  }

  const handleSignOut = () => {
    // Both local and local-encrypted are destructive — auth.logout() calls
    // clearAll() which deletes LS_NSEC and LS_NCRYPTSEC. Without a backup
    // nsec the account is unrecoverable.
    if (loginType === 'local' || loginType === 'local-encrypted') {
      const ok = window.confirm(
        'Signing out clears your account from this device. You will need your recovery key to sign in again on this or any other device. Continue?'
      )
      if (!ok) return
    }
    onLogout?.()
    onClose()
  }

  const signOutHint =
    loginType === 'nip07'
      ? 'You can sign back in with your browser extension.'
      : loginType === 'google'
      ? 'You can sign back in with Google — Verdant will reload your Nostr key from your Google Drive. To fully disconnect, use Unlink Google and then revoke access in your Google Account settings.'
      : 'Signing out clears your account from this device. Make sure you have your recovery key saved before continuing.'

  // UX29 Phase 2b: Unlink Google. Soft unlink — logs the user out
  // locally. The ciphertext in the user's Google Drive remains until
  // they revoke Verdant's access via Google Account settings (useless
  // without the server-side KDF salt, but Privacy §2.7 documents the
  // revocation path for users who want a hard disconnect).
  const handleUnlinkGoogle = () => {
    const ok = window.confirm(
      "Unlinking signs you out of Verdant on this device. Your Nostr key stays encrypted in your Google Drive — to delete it entirely, revoke Verdant's access in your Google Account settings. If you want to sign back in later without Google, make sure you've exported your recovery key first. Continue?"
    )
    if (!ok) return
    onLogout?.()
    onClose()
  }

  // Profile edit helpers.
  const startEditing = () => {
    setForm({
      displayName: profile?.displayName || '',
      name: profile?.name || '',
      picture: profile?.picture || '',
      nip05: profile?.nip05 || '',
      about: profile?.about || '',
    })
    setSaveError(null)
    setEditing(true)
  }

  const cancelEditing = () => {
    setEditing(false)
    setSaveError(null)
  }

  const handleUnlockClick = () => {
    onUnlock?.()
    onClose()
  }

  const handleSave = async (e) => {
    e.preventDefault()
    if (!onUpdateProfile) {
      setSaveError('Profile editing is not configured.')
      return
    }
    setSaving(true)
    setSaveError(null)
    try {
      await onUpdateProfile(form)
      setEditing(false)
    } catch (err) {
      const msg = String(err?.message || err || 'Unknown error')
      if (/reject|denied|cancel/i.test(msg)) {
        setSaveError('Signing canceled. Try again when ready.')
      } else {
        setSaveError(msg)
      }
    } finally {
      setSaving(false)
    }
  }

  // Inline validation warnings. Don't block save; let the user override.
  const pictureWarning =
    form.picture && !/^https?:\/\/\S+/i.test(form.picture)
      ? 'Picture URL should start with https://'
      : null
  const nip05Warning =
    form.nip05 && !isLikelyValidNip05(form.nip05)
      ? "Doesn't look like a valid NIP-05 (format: name@example.com)"
      : null

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="account-title"
    >
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-head">
          <h2 id="account-title">
            {exported ? 'Your recovery key' : 'Your account'}
          </h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Close"
          >×</button>
        </header>

        {exported ? (
          <BackupView
            generated={exported}
            encrypted={loginType === 'local-encrypted'}
            onDone={() => setExported(null)}
          />
        ) : (
          <div className="modal-body">
            <div className="account-identity">
              {showImg ? (
                <img
                  className="account-avatar"
                  src={profile.picture}
                  alt=""
                  referrerPolicy="no-referrer"
                  onError={() => setImgFailed(true)}
                />
              ) : (
                <div className="account-avatar-fallback" aria-hidden="true">{initial}</div>
              )}
              <div className="account-identity-text">
                <div className="account-display-name">{label}</div>
                {profile?.nip05 && (
                  <Nip05Display
                    nip05={profile.nip05}
                    verified={nip05Verified}
                    className="account-identity-nip05"
                  />
                )}
                {loginType === 'google' && (
                  <div
                    className="account-source-badge"
                    title="You signed in with Google. Your Nostr key is stored encrypted in your own Google Drive appData folder."
                  >
                    Linked via Google
                  </div>
                )}
              </div>
            </div>

            <section className="account-section">
              <h3 className="account-section-title">Profile</h3>
              {editing ? (
                <form className="account-edit-form" onSubmit={handleSave}>
                  <div className="field">
                    <label>Display name</label>
                    <input
                      type="text"
                      value={form.displayName}
                      onChange={(e) => setForm({ ...form, displayName: e.target.value })}
                      placeholder="What people see, e.g. Steve Holden Corbett"
                      disabled={saving}
                    />
                  </div>
                  <div className="field">
                    <label>Handle</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="Short username, e.g. steve"
                      disabled={saving}
                    />
                  </div>
                  <div className="field">
                    <label>Picture</label>
                    {form.picture && (
                      <div className="account-pic-preview">
                        <img src={form.picture} alt="" referrerPolicy="no-referrer" />
                      </div>
                    )}
                    <div className="account-pic-controls">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={
                          saving ||
                          picUploadStatus === 'uploading' ||
                          typeof onSign !== 'function'
                        }
                      >
                        {picUploadStatus === 'uploading'
                          ? 'Uploading…'
                          : form.picture ? 'Replace picture' : 'Upload picture'}
                      </button>
                      {form.picture && !saving && picUploadStatus !== 'uploading' && (
                        <button
                          type="button"
                          className="link-button"
                          onClick={() => setForm({ ...form, picture: '' })}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePictureUpload}
                      style={{ display: 'none' }}
                    />
                    {picUploadError && (
                      <div className="note warning">{picUploadError}</div>
                    )}
                    {!showPicUrl ? (
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => setShowPicUrl(true)}
                        style={{ marginTop: 6, padding: 0 }}
                      >
                        Paste URL instead
                      </button>
                    ) : (
                      <input
                        type="url"
                        value={form.picture}
                        onChange={(e) => setForm({ ...form, picture: e.target.value })}
                        placeholder="https://..."
                        disabled={saving}
                        style={{ marginTop: 6 }}
                      />
                    )}
                    {pictureWarning && showPicUrl && (
                      <div className="note warning">{pictureWarning}</div>
                    )}
                  </div>
                  <div className="field">
                    <label>NIP-05 identifier</label>
                    <input
                      type="text"
                      value={form.nip05}
                      onChange={(e) => setForm({ ...form, nip05: e.target.value })}
                      placeholder="alice@example.com"
                      disabled={saving}
                    />
                    {nip05Warning && (
                      <div className="note warning">{nip05Warning}</div>
                    )}
                  </div>
                  <div className="field">
                    <label>
                      About <span className="account-edit-count">
                        ({form.about.length}/{ABOUT_LIMIT})
                      </span>
                    </label>
                    <textarea
                      rows={3}
                      value={form.about}
                      onChange={(e) => setForm({ ...form, about: e.target.value })}
                      placeholder="A short bio"
                      maxLength={Math.max(ABOUT_LIMIT, form.about.length)}
                      disabled={saving}
                    />
                  </div>
                  <div className="account-edit-actions">
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={cancelEditing}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                  {saveError && (
                    <div className="error-state" style={{ marginTop: 12 }}>
                      {saveError}
                    </div>
                  )}
                </form>
              ) : (
                <>
                  <dl className="account-edit-display">
                    <dt>Display name</dt>
                    <dd>{profile?.displayName || <em>not set</em>}</dd>
                    <dt>Handle</dt>
                    <dd>{profile?.name || <em>not set</em>}</dd>
                    <dt>Picture URL</dt>
                    <dd>{profile?.picture ? <code>{profile.picture}</code> : <em>not set</em>}</dd>
                    <dt>NIP-05</dt>
                    <dd>{profile?.nip05 || <em>not set</em>}</dd>
                    <dt>About</dt>
                    <dd>{profile?.about || <em>not set</em>}</dd>
                  </dl>
                  {locked ? (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleUnlockClick}
                    >
                      Unlock to edit
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={startEditing}
                      disabled={!onUpdateProfile}
                      title={onUpdateProfile ? '' : 'Profile editing not configured'}
                    >
                      Edit profile
                    </button>
                  )}
                </>
              )}
            </section>

            <section className="account-section">
              <h3 className="account-section-title">Public key</h3>
              <div className="account-npub">
                <code className="account-npub-text">{npub || ''}</code>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={copyNpub}
                  disabled={!npub}
                >
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p className="note" style={{ marginTop: 8 }}>
                Share this freely. Anyone on Nostr can find you with it.
              </p>
            </section>

            {canExportNsec && (
              <section className="account-section">
                <h3 className="account-section-title">Recovery key</h3>
                <p className="note" style={{ marginBottom: 8 }}>
                  View your recovery key so you can save it to a password
                  manager, sign in from another device, or recover your
                  account if you forget your passphrase.
                </p>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleExport}
                >
                  Export recovery key
                </button>
                {exportError && (
                  <div className="error-state" style={{ marginTop: 8 }}>
                    {exportError}
                  </div>
                )}
              </section>
            )}

            {canLinkGoogle && (
              <section className={`account-section ${loginType === 'ephemeral' ? 'account-section-call-out' : ''}`}>
                <h3 className="account-section-title">
                  {loginType === 'ephemeral' ? 'Save your account' : 'Link Google account'}
                </h3>
                <p className="note" style={{ marginBottom: 8 }}>
                  {loginType === 'ephemeral' ? (
                    <>You&rsquo;re voting anonymously on this device. Save your
                    account with Google so your votes travel with you.
                    The key is stored encrypted in your own Google Drive —
                    Verdant never sees it.</>
                  ) : (
                    <>Link your current Nostr key to a Google account so you can
                    sign back in on another device without entering your
                    passphrase. The key is encrypted and stored in your own
                    Google Drive — Verdant never sees it.</>
                  )}
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={onLinkGoogle}
                  disabled={!onLinkGoogle}
                >
                  {loginType === 'ephemeral' ? 'Save with Google' : 'Link Google account'}
                </button>
              </section>
            )}

            {loginType === 'google' && (
              <section className="account-section">
                <h3 className="account-section-title">Unlink Google</h3>
                <p className="note" style={{ marginBottom: 8 }}>
                  Disconnect this device from Google sign-in. Your Nostr
                  key remains encrypted in your Google Drive; delete it
                  entirely by revoking Verdant's access in your Google
                  Account settings.
                </p>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleUnlinkGoogle}
                >
                  Unlink Google
                </button>
              </section>
            )}

            <section className="account-section">
              <h3 className="account-section-title">Sign out</h3>
              <p className="note" style={{ marginBottom: 8 }}>{signOutHint}</p>
              <button
                type="button"
                className="btn account-signout-btn"
                onClick={handleSignOut}
              >
                Sign out
              </button>
            </section>
          </div>
        )}
      </div>
    </div>
  )
}
